import { useMemo } from 'react';

import { formatRelativeTime } from '@/lib/utils/timeFormatting';
import type { CrmObject } from '@/types/crm';

import type { HighlightDefinition } from '../registry/entityTypeRegistry';
import { EMPTY_LABELS } from './constants';
import { HighlightCardShell } from './HighlightCardShell';
import { LAST_PROPERTY_UPDATE_ICON } from './highlightIcons';

type LastPropertyUpdateDefinition = Extract<
  HighlightDefinition,
  { kind: 'last_property_update' }
>;

interface Props {
  definition: LastPropertyUpdateDefinition;
  object: CrmObject;
  orgId: number;
}

export function LastPropertyUpdateHighlightCard({ definition, object }: Props) {
  const result = useMemo(() => {
    const names = definition.propertyNames ?? [];

    const timestamps = names
      .map(n => object.properties?.[n]?.updated_at)
      .filter((v): v is string => typeof v === 'string' && v.length > 0)
      .map(s => new Date(s).getTime())
      .filter(t => !isNaN(t));

    const value =
      timestamps.length > 0
        ? formatRelativeTime(new Date(Math.max(...timestamps)).toISOString())
        : EMPTY_LABELS.last_property_update;

    return { value, isLoading: false as const, isError: false as const };
  }, [definition.propertyNames, object.properties]);

  return (
    <HighlightCardShell
      label={definition.label}
      result={result}
      icon={LAST_PROPERTY_UPDATE_ICON}
    />
  );
}
